import React, { createContext, useReducer, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import apis from "./api";

export const NotificationContext = createContext();

const initialState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  filter: "all", // all, unread, read
  limit: 20,
  offset: 0,
  total: 0,
};

const notificationReducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload, isLoading: false };
    case "SET_NOTIFICATIONS":
      return {
        ...state,
        notifications: action.payload.notifications,
        unreadCount: action.payload.unreadCount,
        total: action.payload.total,
        isLoading: false,
        error: null,
      };
    case "APPEND_NOTIFICATIONS":
      return {
        ...state,
        notifications: [...state.notifications, ...action.payload.notifications],
        offset: state.offset + state.limit,
        isLoading: false,
      };
    case "SET_UNREAD_COUNT":
      return { ...state, unreadCount: action.payload };
    case "UPDATE_NOTIFICATION":
      return {
        ...state,
        notifications: state.notifications.map((n) =>
          n.id === action.payload.id ? { ...n, ...action.payload } : n
        ),
      };
    case "SET_FILTER":
      return { ...state, filter: action.payload, offset: 0 };
    case "RESET_OFFSET":
      return { ...state, offset: 0 };
    default:
      return state;
  }
};

export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  const fetchNotifications = useCallback(async (limit = 20, offset = 0, filter = "all") => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const isRead = filter === "all" ? null : filter === "unread" ? 0 : 1;
      const data = await apis.getNotifications(limit, offset, isRead);
      dispatch({
        type: "SET_NOTIFICATIONS",
        payload: {
          notifications: data.notifications || [],
          unreadCount: data.unreadCount || 0,
          total: data.total || 0,
        },
      });
    } catch (err) {
      dispatch({ type: "SET_ERROR", payload: err.message });
    }
  }, []);

  const loadMoreNotifications = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const isRead = state.filter === "all" ? null : state.filter === "unread" ? 0 : 1;
      const data = await apis.getNotifications(state.limit, state.offset + state.limit, isRead);
      dispatch({
        type: "APPEND_NOTIFICATIONS",
        payload: { notifications: data.notifications || [] },
      });
    } catch (err) {
      dispatch({ type: "SET_ERROR", payload: err.message });
    }
  }, [state.filter, state.limit, state.offset]);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const data = await apis.getUnreadCount();
      dispatch({ type: "SET_UNREAD_COUNT", payload: data.unreadCount || 0 });
    } catch (err) {
      console.error("Failed to refresh unread count:", err);
    }
  }, []);

  const markAsRead = useCallback(
    async (notificationId, isRead = true) => {
      try {
        await apis.markNotificationAsRead(notificationId, isRead);
        dispatch({
          type: "UPDATE_NOTIFICATION",
          payload: { id: notificationId, isRead },
        });
        refreshUnreadCount();
      } catch (err) {
        dispatch({ type: "SET_ERROR", payload: err.message });
      }
    },
    [refreshUnreadCount]
  );

  const setFilter = useCallback((filter) => {
    dispatch({ type: "SET_FILTER", payload: filter });
    dispatch({ type: "RESET_OFFSET" });
  }, []);

  // Auto-refresh unread count every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refreshUnreadCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [refreshUnreadCount]);

  const value = {
    ...state,
    fetchNotifications,
    loadMoreNotifications,
    refreshUnreadCount,
    markAsRead,
    setFilter,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useNotifications = () => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
};
